/**
 * Ephemeral presence — direct from browser to Momento.
 *
 * Old flow: every ~2 min the browser POSTed /api/user/presence/heartbeat,
 * which spun up a Lambda, wrote DynamoDB, and published a Momento topic
 * event. That's three billable operations per heartbeat for a value that
 * decays naturally — "user is online right now" doesn't need to survive
 * an API outage.
 *
 * New flow:
 *   1. Browser writes `presence:{userId}:live` to the Momento cache with a
 *      15-min TTL. (Direct cache write, scoped via disposable token.)
 *   2. Browser publishes `presence.{userId}` on the presence topic so
 *      already-subscribed peers learn about state changes instantly.
 *   3. Backend `touchPresence` is called *on real API actions* (sending a
 *      message, posting, reacting, etc.) and writes DynamoDB at most once
 *      per 15 minutes per user. The DB row is the slow long-tail "last
 *      seen" record, not the live status.
 *
 * Cost: 1 cache write + 1 topic publish per heartbeat, no Lambda, no DDB
 * write. Browser side never makes an HTTP call on the heartbeat path.
 */

import { getRealtimeTransport, rtEnabled } from "@slumbr/core";

import {
  getDirectMomentoCacheClient,
  getDirectMomentoTopicClient,
} from "@/apps/messenger/momento-subscriber";
import { usePresenceStore } from "@/stores/presence-store";

const LIVE_TTL_SECONDS = 15 * 60; // 15 minutes
const LAST_SEEN_TTL_SECONDS = 90 * 24 * 60 * 60; // 90 days
const HEARTBEAT_MS = 60_000; // 1 minute while active
const BACKGROUND_HEARTBEAT_MS = 5 * 60_000; // 5 minutes when tab hidden

export type PresenceVisibility = "online" | "away" | "busy" | "offline";

export interface EphemeralPresenceRecord {
  userId: string;
  visibility: PresenceVisibility;
  effectiveStatus: PresenceVisibility;
  online: boolean;
  lastSeen: string;
  updatedAt: string;
}

function presenceCacheKey(userId: string): string {
  return `presence:${userId}:live`;
}

function presenceLastSeenKey(userId: string): string {
  return `presence:${userId}:lastSeen`;
}

function presenceTopicName(prefix: string, userId: string): string {
  return `${prefix}.presence.${userId}`;
}

export async function publishEphemeralPresence(
  userId: string,
  visibility: PresenceVisibility
): Promise<void> {
  if (!userId) return;
  const nowIso = new Date().toISOString();
  const record: EphemeralPresenceRecord = {
    userId,
    visibility,
    effectiveStatus: visibility,
    online: visibility !== "offline",
    lastSeen: nowIso,
    updatedAt: nowIso,
  };
  usePresenceStore.getState().setLocalSelfStatus(record);
  try {
    const { client, cacheName } = await getDirectMomentoCacheClient();
    if (record.online) {
      await Promise.all([
        client.set(cacheName, presenceCacheKey(userId), JSON.stringify(record), {
          ttl: LIVE_TTL_SECONDS,
        }),
        client.set(cacheName, presenceLastSeenKey(userId), record.lastSeen, {
          ttl: LAST_SEEN_TTL_SECONDS,
        }),
      ]);
    } else {
      await client.delete(cacheName, presenceCacheKey(userId));
    }
  } catch (err) {
    console.warn("[presence] direct cache write failed", err);
  }
  try {
    const { client, cacheName, token } = await getDirectMomentoTopicClient();
    await client.publish(
      cacheName,
      presenceTopicName(token.topicPrefix ?? "slumbr", userId),
      JSON.stringify({ type: "presence.updated", userId, status: record })
    );
  } catch (err) {
    console.warn("[presence] topic publish failed", err);
  }
}

export async function readEphemeralPresence(
  userIds: string[]
): Promise<Map<string, EphemeralPresenceRecord>> {
  const result = new Map<string, EphemeralPresenceRecord>();
  if (userIds.length === 0) return result;
  try {
    const { client, cacheName } = await getDirectMomentoCacheClient();
    await Promise.all(
      userIds.map(async (uid) => {
        const resp = await client.get(cacheName, presenceCacheKey(uid));
        // Momento SDK uses tagged response classes; check for valueString.
        const value = (resp as { valueString?: () => string }).valueString?.();
        if (!value) return;
        try {
          const parsed = JSON.parse(value) as EphemeralPresenceRecord;
          if (parsed.userId === uid) result.set(uid, parsed);
        } catch {
          // ignore malformed entries
        }
      })
    );
  } catch (err) {
    console.warn("[presence] direct cache read failed", err);
  }
  return result;
}

export interface EphemeralPresenceController {
  stop: () => void;
}

/**
 * Start ephemeral-presence heartbeat for the signed-in user. Returns a
 * disposer. Caller is responsible for ensuring this only runs on the
 * tab-leader.
 */
/**
 * Durable-Object presence (flag: VITE_RT_PRESENCE). Holds one WS to the app
 * PresenceDO; the socket's existence IS our presence (no heartbeat spam). The
 * DO pushes snapshot/join/leave, which we fold into the presence store. The
 * Momento path below stays the default + instant rollback.
 */
function startDurableObjectPresence(userId: string): EphemeralPresenceController {
  const channel = getRealtimeTransport().join("presence/app");
  const known = new Set<string>();

  const sync = () => {
    const online = channel.presence();
    const nowIso = new Date().toISOString();
    // Mark newly-online peers online.
    for (const uid of online) {
      if (!known.has(uid)) {
        known.add(uid);
        usePresenceStore.getState().setStatus({
          userId: uid,
          visibility: "online",
          effectiveStatus: "online",
          online: true,
          lastSeen: nowIso,
          updatedAt: nowIso,
        });
      }
    }
    // Mark peers that dropped out of the set offline.
    for (const uid of [...known]) {
      if (!online.has(uid)) {
        known.delete(uid);
        usePresenceStore.getState().setStatus({
          userId: uid,
          visibility: "offline",
          effectiveStatus: "offline",
          online: false,
          lastSeen: nowIso,
          updatedAt: nowIso,
        });
      }
    }
  };

  usePresenceStore.getState().setLocalSelfStatus({
    userId,
    visibility: "online",
    effectiveStatus: "online",
    online: true,
    lastSeen: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  const off = channel.on(() => sync());
  return {
    stop: () => {
      off();
      channel.close();
    },
  };
}

export function startEphemeralPresence(userId: string): EphemeralPresenceController {
  if (rtEnabled("presence")) {
    return startDurableObjectPresence(userId);
  }
  let stopped = false;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let lastPublishedVisibility: PresenceVisibility | null = null;

  const tick = () => {
    if (stopped) return;
    const visibility = usePresenceStore.getState().visibility;
    if (visibility === "offline" && lastPublishedVisibility === "offline") return;
    lastPublishedVisibility = visibility;
    void publishEphemeralPresence(userId, visibility);
  };

  const setCadence = () => {
    if (intervalId) clearInterval(intervalId);
    const ms = document.visibilityState === "hidden" ? BACKGROUND_HEARTBEAT_MS : HEARTBEAT_MS;
    intervalId = setInterval(tick, ms);
  };

  const onVisibility = () => {
    setCadence();
    tick();
  };

  // `focus` covers the wake-from-AFK case where the tab was always
  // visible but the user came back from another window. `visibilitychange`
  // doesn't fire in that scenario, so without this listener the next tick
  // could be up to one HEARTBEAT_MS away — long enough for other users to
  // see our cache key expire.
  const onFocus = () => tick();

  // Fire a tick on `online` (regaining network) so we re-publish presence
  // immediately rather than waiting for the next interval. Otherwise after
  // a brief offline blip other users would continue seeing us as offline
  // for up to 60 s.
  const onOnline = () => tick();

  tick();
  setCadence();
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", onFocus);
  window.addEventListener("online", onOnline);
  const unsubscribePresence = usePresenceStore.subscribe((state, prevState) => {
    if (state.visibility !== prevState.visibility) tick();
  });

  return {
    stop: () => {
      stopped = true;
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      unsubscribePresence();
    },
  };
}
